import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  XCircle,
  Award,
  Clock,
  FileText,
  PlayCircle,
  BookOpen
} from 'lucide-react';
import Navigation from '@/components/landing/Navigation';
import Footer from '@/components/landing/Footer';
import { simulatedExams, testQuestions } from '@/mockData';

const SimulatedExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  
  const exam = simulatedExams[examId];
  
  const [examStarted, setExamStarted] = useState(false);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sectionAnswers, setSectionAnswers] = useState({});
  const [examCompleted, setExamCompleted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Initialize answers for all sections (must be called before any early return
  // to keep hook order stable across renders).
  useEffect(() => {
    if (!examStarted || !exam) return;

    const initialAnswers = {};
    exam.sections.forEach(section => {
      if (!sectionAnswers[section.testId]) {
        initialAnswers[section.testId] = {};
      }
    });
    if (Object.keys(initialAnswers).length > 0) {
      setSectionAnswers(prev => ({ ...prev, ...initialAnswers }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examStarted, exam]);

  if (!exam) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Card className="p-8 text-center max-w-md">
          <XCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Exam Not Found</h2>
          <Button onClick={() => navigate('/resources')}>
            Back to Resources
          </Button>
        </Card>
      </div>
    );
  }

  const currentSection = exam.sections[currentSectionIndex];
  const currentTest = testQuestions[currentSection?.testId];
  const currentQuestion = currentTest?.questions[currentQuestionIndex];

  const handleAnswerSelect = (answerIndex) => {
    setSectionAnswers(prev => ({
      ...prev,
      [currentSection.testId]: {
        ...prev[currentSection.testId],
        [currentQuestionIndex]: answerIndex
      }
    }));
  };

  const handleNext = () => {
    if (currentQuestionIndex < currentTest.questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextSection = () => {
    if (currentSectionIndex < exam.sections.length - 1) {
      setCurrentSectionIndex(currentSectionIndex + 1);
      setCurrentQuestionIndex(0);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCompleteExam = () => {
    setExamCompleted(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const calculateSectionScore = (testId) => {
    const test = testQuestions[testId];
    const answers = sectionAnswers[testId] || {};
    let correct = 0;
    
    test.questions.forEach((q, index) => {
      if (answers[index] === q.correctAnswer) {
        correct++;
      }
    });
    
    return {
      correct,
      total: test.questions.length,
      percentage: Math.round((correct / test.questions.length) * 100)
    };
  };

  const calculateOverallScore = () => {
    let totalCorrect = 0;
    let totalQuestions = 0;
    
    exam.sections.forEach(section => {
      const score = calculateSectionScore(section.testId);
      totalCorrect += score.correct;
      totalQuestions += score.total;
    });
    
    return {
      correct: totalCorrect,
      total: totalQuestions,
      percentage: Math.round((totalCorrect / totalQuestions) * 100)
    };
  };

  const isCurrentSectionComplete = () => {
    const answers = sectionAnswers[currentSection.testId] || {};
    return Object.keys(answers).length === currentTest.questions.length;
  };

  // Landing Page
  if (!examStarted) {
    return (
      <>
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            <Button
              variant="ghost"
              onClick={() => navigate(`/resources/${exam.category}`)}
              className="mb-6"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Resources
            </Button>

            <Card className="p-8 mb-6" style={{ borderTop: '4px solid #006C69' }}>
              <div className="flex items-start gap-4 mb-6">
                <div className="w-16 h-16 rounded-lg flex items-center justify-center" style={{ background: '#006C69' }}>
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <div className="flex-1">
                  <h1 className="text-3xl font-bold mb-2">{exam.title}</h1>
                  <p className="text-gray-600 mb-4">{exam.description}</p>
                  <div className="flex flex-wrap gap-3">
                    <Badge style={{ background: '#006C69' }}>
                      <Clock className="w-3 h-3 mr-1" />
                      {exam.duration}
                    </Badge>
                    <Badge style={{ background: '#B7BF72', color: '#000' }}>
                      {exam.sections.length} Sections
                    </Badge>
                    {exam.isPremium === false && (
                      <Badge style={{ background: '#10b981', color: '#fff' }}>
                        100% FREE
                      </Badge>
                    )}
                  </div>
                </div>
              </div>

              {/* Sections Overview */}
              <div className="mb-6">
                <h3 className="text-lg font-bold mb-3">Test Sections:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {exam.sections.map((section, index) => (
                    <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center font-bold" style={{ background: '#006C69', color: '#fff' }}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-sm">{section.sectionName}</div>
                        <div className="text-xs text-gray-600">{section.duration}</div>
                      </div>
                      <BookOpen className="w-4 h-4 text-gray-400" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Instructions */}
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                <h4 className="font-bold text-blue-900 mb-2">Important Instructions:</h4>
                <ul className="space-y-1 text-sm text-blue-800">
                  {exam.instructions.map((instruction, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      <span>{instruction}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <Button
                onClick={() => setExamStarted(true)}
                className="w-full h-14 text-lg font-bold"
                style={{ background: '#006C69' }}
              >
                <PlayCircle className="w-5 h-5 mr-2" />
                Start Exam Simulation
              </Button>
            </Card>
          </div>
        </div>
        
        <Footer />
      </>
    );
  }

  // Results Page
  if (examCompleted) {
    const overallScore = calculateOverallScore();
    
    return (
      <>
        <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
        
        <div className="min-h-screen bg-gray-50 pt-24 pb-12">
          <div className="max-w-4xl mx-auto px-6">
            {/* Overall Results */}
            <Card className="p-8 mb-8 text-center" style={{ borderTop: '4px solid #006C69' }}>
              <Award className="w-20 h-20 mx-auto mb-4" style={{ color: '#B7BF72' }} />
              <h1 className="text-3xl font-bold mb-2">Exam Complete!</h1>
              <p className="text-lg text-gray-600 mb-6">{exam.title}</p>
              
              <div className="grid grid-cols-3 gap-6 max-w-2xl mx-auto mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-4xl font-bold" style={{ color: '#006C69' }}>
                    {overallScore.percentage}%
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Overall Score</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-4xl font-bold text-green-600">
                    {overallScore.correct}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Correct</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <div className="text-4xl font-bold text-red-600">
                    {overallScore.total - overallScore.correct}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">Incorrect</div>
                </div>
              </div>

              {overallScore.percentage >= 80 && (
                <Badge className="text-base px-4 py-2 mb-4" style={{ background: '#006C69' }}>
                  🎉 Excellent Performance!
                </Badge>
              )}
              {overallScore.percentage >= 60 && overallScore.percentage < 80 && (
                <Badge className="text-base px-4 py-2 mb-4" style={{ background: '#B7BF72', color: '#000' }}>
                  👍 Good Job! Keep Practicing
                </Badge>
              )}
              {overallScore.percentage < 60 && (
                <Badge className="text-base px-4 py-2 mb-4" variant="secondary">
                  📚 Review and practice more
                </Badge>
              )}
            </Card>

            {/* Section Breakdown */}
            <h2 className="text-2xl font-bold mb-4">Section Breakdown</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {exam.sections.map((section, index) => {
                const score = calculateSectionScore(section.testId);
                return (
                  <Card key={index} className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold" style={{ background: '#006C69', color: '#fff' }}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold">{section.sectionName}</h3>
                        <div className="text-sm text-gray-600">{testQuestions[section.testId].totalQuestions} questions</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">Score:</span>
                      <span className="text-2xl font-bold" style={{ color: score.percentage >= 70 ? '#10b981' : '#ef4444' }}>
                        {score.percentage}%
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      {score.correct} / {score.total} correct
                    </div>
                  </Card>
                );
              })}
            </div>

            {/* Detailed Review */}
            <h2 className="text-2xl font-bold mb-4">Detailed Answer Review</h2>
            {exam.sections.map((section, sectionIdx) => {
              const test = testQuestions[section.testId];
              const answers = sectionAnswers[section.testId] || {};
              
              return (
                <div key={sectionIdx} className="mb-8">
                  <h3 className="text-xl font-bold mb-4" style={{ color: '#006C69' }}>
                    Section {sectionIdx + 1}: {section.sectionName}
                  </h3>
                  <div className="space-y-4">
                    {test.questions.map((q, qIdx) => {
                      const userAnswer = answers[qIdx];
                      const isCorrect = userAnswer === q.correctAnswer;
                      
                      return (
                        <Card key={qIdx} className="p-6">
                          <div className="flex items-start gap-3 mb-3">
                            {isCorrect ? (
                              <CheckCircle2 className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                            ) : (
                              <XCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                            )}
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 mb-2">
                                Question {qIdx + 1}: {q.question}
                              </div>
                              
                              <div className="space-y-2 mb-3">
                                {q.options.map((option, optIdx) => {
                                  const isUserAnswer = userAnswer === optIdx;
                                  const isCorrectAnswer = q.correctAnswer === optIdx;
                                  
                                  let className = 'p-3 rounded-lg border ';
                                  if (isCorrectAnswer) {
                                    className += 'bg-green-50 border-green-300';
                                  } else if (isUserAnswer && !isCorrect) {
                                    className += 'bg-red-50 border-red-300';
                                  } else {
                                    className += 'bg-gray-50 border-gray-200';
                                  }
                                  
                                  return (
                                    <div key={optIdx} className={className}>
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-sm">
                                          {String.fromCharCode(65 + optIdx)}.
                                        </span>
                                        <span className="text-sm">{option}</span>
                                        {isCorrectAnswer && (
                                          <Badge className="ml-auto" style={{ background: '#006C69' }}>
                                            Correct
                                          </Badge>
                                        )}
                                        {isUserAnswer && !isCorrect && (
                                          <Badge className="ml-auto" variant="destructive">
                                            Your Answer
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              
                              {q.explanation && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                  <div className="text-sm font-semibold text-blue-900 mb-1">
                                    Explanation:
                                  </div>
                                  <div className="text-sm text-blue-800">{q.explanation}</div>
                                </div>
                              )}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })}

            <div className="flex gap-4 justify-center mt-8">
              <Button
                onClick={() => window.location.reload()}
                className="font-semibold"
                style={{ background: '#006C69' }}
              >
                Retry Exam
              </Button>
              <Button
                onClick={() => navigate(`/resources/${exam.category}`)}
                variant="outline"
                className="font-semibold"
              >
                Back to Resources
              </Button>
            </div>
          </div>
        </div>
        
        <Footer />
      </>
    );
  }

  // Exam in Progress
  const totalQuestions = exam.sections.reduce((sum, section) => 
    sum + testQuestions[section.testId].questions.length, 0
  );
  const answeredSoFar = Object.values(sectionAnswers).reduce((sum, sectionAns) => 
    sum + Object.keys(sectionAns).length, 0
  );
  const overallProgress = (answeredSoFar / totalQuestions) * 100;

  return (
    <>
      <Navigation mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />
      
      <div className="min-h-screen bg-gray-50 pt-24 pb-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Exam Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h1 className="text-2xl font-bold">{exam.title}</h1>
              <Badge style={{ background: '#B7BF72', color: '#000' }}>
                Section {currentSectionIndex + 1} of {exam.sections.length}
              </Badge>
            </div>
            <Progress value={overallProgress} className="h-2 mb-2" />
            <div className="text-sm text-gray-600 text-right">
              {Math.round(overallProgress)}% Complete
            </div>
          </div>

          {/* Section Header */}
          <Card className="p-6 mb-6" style={{ borderLeft: '4px solid #006C69' }}>
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">{currentSection.sectionName}</h2>
                <p className="text-sm text-gray-600">
                  Question {currentQuestionIndex + 1} of {currentTest.questions.length}
                </p>
              </div>
              <Badge variant="outline">
                <Clock className="w-3 h-3 mr-1" />
                {currentSection.duration}
              </Badge>
            </div>
          </Card>

          {/* Question Card */}
          <Card className="p-8 mb-6">
            <div className="mb-6">
              <div className="text-sm font-semibold text-gray-500 mb-2">
                QUESTION {currentQuestionIndex + 1}
              </div>
              <h2 className="text-xl font-bold text-gray-900">{currentQuestion.question}</h2>
            </div>

            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => {
                const isSelected = sectionAnswers[currentSection.testId]?.[currentQuestionIndex] === index;
                
                return (
                  <button
                    key={index}
                    onClick={() => handleAnswerSelect(index)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-[#006C69] bg-[#006C69]/5'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected
                            ? 'border-[#006C69] bg-[#006C69]'
                            : 'border-gray-300'
                        }`}
                      >
                        {isSelected && (
                          <CheckCircle2 className="w-4 h-4 text-white" />
                        )}
                      </div>
                      <div className="flex-1">
                        <span className="font-medium mr-2">
                          {String.fromCharCode(65 + index)}.
                        </span>
                        <span className="text-gray-900">{option}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </Card>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button
              onClick={handlePrevious}
              disabled={currentQuestionIndex === 0 && currentSectionIndex === 0}
              variant="outline"
              className="font-semibold"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Previous
            </Button>

            <div className="text-sm text-gray-600">
              {Object.keys(sectionAnswers[currentSection.testId] || {}).length} / {currentTest.questions.length} answered
            </div>

            {currentQuestionIndex < currentTest.questions.length - 1 ? (
              <Button
                onClick={handleNext}
                className="font-semibold"
                style={{ background: '#006C69' }}
              >
                Next
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : currentSectionIndex < exam.sections.length - 1 ? (
              <Button
                onClick={handleNextSection}
                disabled={!isCurrentSectionComplete()}
                className="font-semibold"
                style={{ background: '#B7BF72', color: '#000' }}
              >
                Next Section
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleCompleteExam}
                disabled={!isCurrentSectionComplete()}
                className="font-semibold"
                style={{ background: '#B7BF72', color: '#000' }}
              >
                Complete Exam
                <CheckCircle2 className="w-4 h-4 ml-2" />
              </Button>
            )}
          </div>

          {/* Section Completion Warning */}
          {!isCurrentSectionComplete() && currentQuestionIndex === currentTest.questions.length - 1 && (
            <Card className="mt-4 p-4 bg-amber-50 border-amber-200">
              <p className="text-sm text-amber-800">
                ⚠️ Please answer all questions before moving to the next section.
              </p>
            </Card>
          )}
        </div>
      </div>
      
      <Footer />
    </>
  );
};

export default SimulatedExam;
